import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { QRPreview } from "@/components/QRPreview";
import { QR_TYPES } from "@/lib/qrTypes";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { tagColor } from "@/lib/tags";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Plus, QrCode, MousePointerClick, LayoutGrid, BarChart3, Trash2, Pencil, Copy, ExternalLink, Folder, FolderPlus, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const [qrs, setQrs] = useState([]);
  const [stats, setStats] = useState(null);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderDialog, setFolderDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkFolder, setBulkFolder] = useState("none");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, overview, fldrs] = await Promise.all([
        api.get("/qrcodes"),
        api.get("/qrcodes/stats/overview"),
        api.get("/folders"),
      ]);
      setQrs(list.data);
      setStats(overview.data);
      setFolders(fldrs.data);
    } catch (e) {
      toast.error("Failed to load your QR codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    await api.post("/folders", { name: newFolderName.trim() });
    setNewFolderName("");
    setFolderDialog(false);
    toast.success("Folder created");
    load();
  };

  const deleteFolder = async (fid) => {
    await api.delete(`/folders/${fid}`);
    if (activeFolder === fid) setActiveFolder("all");
    toast.success("Folder deleted");
    load();
  };

  const runBulk = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const items = lines.map((line) => {
      const parts = line.split(",");
      if (parts.length >= 2) return { name: parts[0].trim(), url: parts.slice(1).join(",").trim() };
      return { name: parts[0].trim(), url: parts[0].trim() };
    }).filter((i) => i.url);
    if (!items.length) return toast.error("Add at least one link");
    setBulkBusy(true);
    try {
      const { data } = await api.post("/qrcodes/bulk", {
        folder_id: bulkFolder === "none" ? null : bulkFolder,
        items,
      });
      toast.success(`${data.created} QR codes created`);
      setBulkText("");
      setBulkDialog(false);
      load();
    } catch (e) {
      toast.error("Bulk import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const visibleQrs = qrs.filter((q) => {
    if (activeFolder === "all") return true;
    if (activeFolder === "unfiled") return !q.folder_id;
    return q.folder_id === activeFolder;
  });

  const toggleActive = async (qr) => {
    await api.put(`/qrcodes/${qr.id}`, { is_active: !qr.is_active });
    setQrs((prev) => prev.map((q) => (q.id === qr.id ? { ...q, is_active: !q.is_active } : q)));
    toast.success(qr.is_active ? "QR code disabled" : "QR code enabled");
  };

  const remove = async (qr) => {
    await api.delete(`/qrcodes/${qr.id}`);
    setQrs((prev) => prev.filter((q) => q.id !== qr.id));
    toast.success("QR code deleted");
    load();
  };

  const copyLink = (qr) => {
    navigator.clipboard.writeText(qr.redirect_url);
    toast.success("Tracking link copied");
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">My QR Codes</h1>
            <p className="text-muted-foreground text-sm mt-1">Manage, track and customize your dynamic QR codes.</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-import-btn"><Upload className="h-4 w-4 mr-2" /> Bulk import</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk import QR codes</DialogTitle>
                  <DialogDescription>One link per line. Optionally add a name before a comma: <span className="font-mono text-xs">Menu, https://site.com/menu</span></DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Textarea
                    data-testid="bulk-textarea"
                    rows={7}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={"Homepage, https://example.com\nMenu, https://example.com/menu\nhttps://example.com/promo"}
                    className="font-mono text-sm"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                      <Upload className="h-4 w-4" /> Import a .csv file
                      <input type="file" accept=".csv,text/csv" className="hidden" onChange={onCsvFile} data-testid="bulk-csv-file" />
                    </label>
                    {folders.length > 0 && (
                      <select value={bulkFolder} onChange={(e) => setBulkFolder(e.target.value)} className="border border-border rounded-md text-sm px-2 py-1.5 bg-background" data-testid="bulk-folder-select">
                        <option value="none">No folder</option>
                        {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={runBulk} disabled={bulkBusy} data-testid="bulk-submit-btn">
                    {bulkBusy ? "Creating..." : "Create QR codes"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={() => navigate("/create")} data-testid="create-qr-btn">
              <Plus className="h-4 w-4 mr-2" /> Create QR
            </Button>
          </div>
        </div>

        {/* Folder filter bar */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="folder-bar">
          <FolderChip label="All" active={activeFolder === "all"} onClick={() => setActiveFolder("all")} count={qrs.length} />
          <FolderChip label="Unfiled" active={activeFolder === "unfiled"} onClick={() => setActiveFolder("unfiled")} count={qrs.filter((q) => !q.folder_id).length} />
          {folders.map((f) => (
            <FolderChip
              key={f.id}
              label={f.name}
              icon={Folder}
              active={activeFolder === f.id}
              onClick={() => setActiveFolder(f.id)}
              count={f.count}
              onDelete={() => deleteFolder(f.id)}
            />
          ))}
          <Dialog open={folderDialog} onOpenChange={setFolderDialog}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors" data-testid="new-folder-btn">
                <FolderPlus className="h-3.5 w-3.5" /> New folder
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a folder</DialogTitle>
                <DialogDescription>Group your QR codes by campaign or project.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Folder name</Label>
                <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="e.g. Summer Campaign" data-testid="folder-name-input" onKeyDown={(e) => e.key === "Enter" && createFolder()} />
              </div>
              <DialogFooter>
                <Button onClick={createFolder} data-testid="folder-create-btn">Create folder</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={LayoutGrid} label="Total QR Codes" value={stats?.total_qr ?? "—"} />
          <StatCard icon={MousePointerClick} label="Total Scans" value={stats?.total_scans ?? "—"} />
          <div className="border border-border rounded-xl bg-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Last 7 days</p>
            </div>
            <div className="h-14">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.timeline || []}>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={(l) => l} />
                  <XAxis dataKey="date" hide />
                  <Line type="monotone" dataKey="scans" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : visibleQrs.length === 0 ? (
          <EmptyState onCreate={() => navigate("/create")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="qr-grid">
            {visibleQrs.map((qr) => {
              const T = QR_TYPES[qr.type] || QR_TYPES.url;
              return (
                <div key={qr.id} className="group border border-border rounded-xl bg-card overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-transform duration-200">
                  <div className="p-5 flex gap-4">
                    <div className="shrink-0 rounded-lg bg-white p-1 border border-border">
                      <QRPreview data={qr.redirect_url} design={qr.design} size={84} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-xs text-primary font-medium mb-1">
                        <T.icon className="h-3.5 w-3.5" /> {T.label}
                      </div>
                      <h3 className="font-display font-semibold truncate" data-testid={`qr-name-${qr.id}`}>{qr.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        <span className="font-semibold text-foreground">{qr.scan_count}</span> scans
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Switch checked={qr.is_active} onCheckedChange={() => toggleActive(qr)} data-testid={`toggle-${qr.id}`} />
                        <span className="text-xs text-muted-foreground">{qr.is_active ? "Active" : "Disabled"}</span>
                      </div>
                      {qr.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {qr.tags.map((t) => {
                            const c = tagColor(t);
                            return <span key={t} className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.fg }}>{t}</span>;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center border-t border-border divide-x divide-border">
                    <Link to={`/qr/${qr.id}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors" data-testid={`analytics-${qr.id}`}>
                      <BarChart3 className="h-3.5 w-3.5" /> Analytics
                    </Link>
                    <button onClick={() => navigate(`/edit/${qr.id}`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors" data-testid={`edit-${qr.id}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => copyLink(qr)} className="flex items-center justify-center px-3 py-2.5 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors" title="Copy link" data-testid={`copy-${qr.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <a href={qr.redirect_url} target="_blank" rel="noreferrer" className="flex items-center justify-center px-3 py-2.5 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors" title="Open link">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="flex items-center justify-center px-3 py-2.5 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors" data-testid={`delete-${qr.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this QR code?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{qr.name}" and all its scan data will be permanently removed. The printed code will stop working.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(qr)} data-testid={`confirm-delete-${qr.id}`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-xl bg-card p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-primary" />
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{label}</p>
      </div>
      <p className="font-display text-3xl font-bold">{value}</p>
    </div>
  );
}

function FolderChip({ label, icon: Icon, active, onClick, count, onDelete }) {
  return (
    <div
      className={`group inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full border text-sm transition-colors cursor-pointer ${
        active ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
      }`}
      onClick={onClick}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span className="font-medium">{label}</span>
      <span className={`text-xs px-1.5 rounded-full ${active ? "bg-primary-foreground/20" : "bg-secondary"}`}>{count}</span>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className={`ml-0.5 rounded-full p-0.5 hover:bg-black/10 ${active ? "" : "opacity-0 group-hover:opacity-100"} transition-opacity`}
          title="Delete folder"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-border rounded-xl bg-card p-16 text-center">
      <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
        <QrCode className="h-7 w-7 text-primary" />
      </div>
      <h3 className="font-display text-xl font-semibold mb-2">No QR codes yet</h3>
      <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">Create your first dynamic QR code and start tracking scans in real time.</p>
      <Button onClick={onCreate} data-testid="empty-create-btn"><Plus className="h-4 w-4 mr-2" /> Create your first QR</Button>
    </div>
  );
}
